import type { CSSProperties } from 'react'

import { paperColors } from '@gto/ui/theme/design-tokens'

import { isSafeAnnouncementColor } from './safe-color'

export type ModalBackgroundSize = 'auto' | 'contain' | 'cover'
export type ModalBackgroundRepeat = 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y'
export type ModalBackgroundMobileMode = 'desktop' | 'mobile' | 'none'

export const MODAL_BACKGROUND_POSITIONS = [
  'left top',
  'center top',
  'right top',
  'left center',
  'center',
  'right center',
  'left bottom',
  'center bottom',
  'right bottom',
] as const
const MODAL_BACKGROUND_PERCENT_POSITION = /^(?:100|[1-9]\d|\d)% (?:100|[1-9]\d|\d)%$/

export type ModalBackground = {
  start?: string | null
  end?: string | null
  /** Degrees, as CSS reads them: 180 runs top to bottom. */
  angle?: number
  size?: ModalBackgroundSize
  position?: string
  repeat?: ModalBackgroundRepeat
  mobile_mode?: ModalBackgroundMobileMode
  image_url?: string | null
  image_mobile_url?: string | null
}

export const MODAL_BACKGROUND_PROPERTY = '--announcement-modal-background'
export const MODAL_BACKGROUND_MOBILE_PROPERTY = '--announcement-modal-background-mobile'
export const MODAL_BACKGROUND_SIZE_PROPERTY = '--announcement-modal-background-size'
export const MODAL_BACKGROUND_SIZE_MOBILE_PROPERTY = '--announcement-modal-background-size-mobile'
export const MODAL_BACKGROUND_POSITION_PROPERTY = '--announcement-modal-background-position'
export const MODAL_BACKGROUND_POSITION_MOBILE_PROPERTY =
  '--announcement-modal-background-position-mobile'
export const MODAL_BACKGROUND_REPEAT_PROPERTY = '--announcement-modal-background-repeat'
export const MODAL_BACKGROUND_REPEAT_MOBILE_PROPERTY =
  '--announcement-modal-background-repeat-mobile'

export const MODAL_BACKGROUND_CLASS =
  'bg-[image:var(--announcement-modal-background)] [background-position:var(--announcement-modal-background-position)] [background-repeat:var(--announcement-modal-background-repeat)] [background-size:var(--announcement-modal-background-size)] md:bg-[image:var(--announcement-modal-background-mobile)] md:[background-position:var(--announcement-modal-background-position-mobile)] md:[background-repeat:var(--announcement-modal-background-repeat-mobile)] md:[background-size:var(--announcement-modal-background-size-mobile)]'

export type ModalBackgroundStyle = CSSProperties &
  Record<
    | typeof MODAL_BACKGROUND_PROPERTY
    | typeof MODAL_BACKGROUND_MOBILE_PROPERTY
    | typeof MODAL_BACKGROUND_SIZE_PROPERTY
    | typeof MODAL_BACKGROUND_SIZE_MOBILE_PROPERTY
    | typeof MODAL_BACKGROUND_POSITION_PROPERTY
    | typeof MODAL_BACKGROUND_POSITION_MOBILE_PROPERTY
    | typeof MODAL_BACKGROUND_REPEAT_PROPERTY
    | typeof MODAL_BACKGROUND_REPEAT_MOBILE_PROPERTY,
    string
  >

/**
 * Starts on the flat surface the modal used to be (`paper.elevation3`) and
 * darkens to `paper.elevation0`, so the top edge is unchanged and the gradient
 * reads as depth rather than a new colour.
 */
export const DEFAULT_MODAL_BACKGROUND = {
  start: paperColors.elevation3,
  end: paperColors.elevation0,
  angle: 180,
  size: 'cover',
  position: 'center',
  repeat: 'no-repeat',
  mobile_mode: 'none',
} as const satisfies Required<
  Pick<ModalBackground, 'angle' | 'end' | 'mobile_mode' | 'position' | 'repeat' | 'size' | 'start'>
>

const toGradient = ({ start, end, angle }: { start: string; end: string; angle: number }): string =>
  `linear-gradient(${angle}deg, ${start} 0%, ${end} 100%)`

const clampAngle = (angle: number): number => {
  if (!Number.isFinite(angle)) {
    return DEFAULT_MODAL_BACKGROUND.angle
  }

  return Math.min(360, Math.max(0, angle))
}

const safeArtworkUrl = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined || value.trim() === '') {
    return null
  }

  try {
    const parsed = new URL(value)

    return ['blob:', 'http:', 'https:'].includes(parsed.protocol) ? parsed.href : null
  } catch {
    return null
  }
}

const withArtwork = (gradient: string, url: string | null): string =>
  url === null ? gradient : `${gradient}, url("${url}")`

const layerValue = (artwork: string | null, value: string): string =>
  artwork === null ? 'auto' : `auto, ${value}`

const repeatValue = (artwork: string | null, value: string): string =>
  artwork === null ? 'no-repeat' : `no-repeat, ${value}`

const positionValue = (artwork: string | null, value: string): string =>
  artwork === null ? '0% 0%' : `0% 0%, ${value}`

export const isSafeModalBackgroundPosition = (value: string): boolean =>
  MODAL_BACKGROUND_POSITIONS.some((position) => position === value) ||
  MODAL_BACKGROUND_PERCENT_POSITION.test(value)

/** The gradient layer for a modal surface. */
export function modalBackgroundImage(background?: ModalBackground | null): string {
  const start = background?.start ?? DEFAULT_MODAL_BACKGROUND.start
  const end = background?.end ?? DEFAULT_MODAL_BACKGROUND.end
  const angle = background?.angle ?? DEFAULT_MODAL_BACKGROUND.angle

  if (!isSafeAnnouncementColor(start) || !isSafeAnnouncementColor(end)) {
    return toGradient(DEFAULT_MODAL_BACKGROUND)
  }

  return toGradient({ start, end, angle: clampAngle(angle) })
}

/** Responsive custom properties consumed by `MODAL_BACKGROUND_CLASS`. */
export function modalBackgroundStyle(background?: ModalBackground | null): ModalBackgroundStyle {
  const gradient = modalBackgroundImage(background)
  const desktopArtwork = safeArtworkUrl(background?.image_url)
  const mobileArtwork =
    background?.mobile_mode === 'desktop'
      ? desktopArtwork
      : background?.mobile_mode === 'mobile'
        ? safeArtworkUrl(background.image_mobile_url)
        : null
  const authoredSize = background?.size
  const size =
    authoredSize === 'auto' || authoredSize === 'contain' || authoredSize === 'cover'
      ? authoredSize
      : DEFAULT_MODAL_BACKGROUND.size
  const position =
    background?.position !== undefined && isSafeModalBackgroundPosition(background.position)
      ? background.position
      : DEFAULT_MODAL_BACKGROUND.position
  const authoredRepeat = background?.repeat
  const repeat =
    authoredRepeat === 'no-repeat' ||
    authoredRepeat === 'repeat' ||
    authoredRepeat === 'repeat-x' ||
    authoredRepeat === 'repeat-y'
      ? authoredRepeat
      : DEFAULT_MODAL_BACKGROUND.repeat

  return {
    [MODAL_BACKGROUND_PROPERTY]: withArtwork(gradient, desktopArtwork),
    [MODAL_BACKGROUND_MOBILE_PROPERTY]: withArtwork(gradient, mobileArtwork),
    [MODAL_BACKGROUND_SIZE_PROPERTY]: layerValue(desktopArtwork, size),
    [MODAL_BACKGROUND_SIZE_MOBILE_PROPERTY]: layerValue(mobileArtwork, size),
    [MODAL_BACKGROUND_POSITION_PROPERTY]: positionValue(desktopArtwork, position),
    [MODAL_BACKGROUND_POSITION_MOBILE_PROPERTY]: positionValue(mobileArtwork, position),
    [MODAL_BACKGROUND_REPEAT_PROPERTY]: repeatValue(desktopArtwork, repeat),
    [MODAL_BACKGROUND_REPEAT_MOBILE_PROPERTY]: repeatValue(mobileArtwork, repeat),
  }
}
